import React from 'react';
import usePiggyBank from 'shared/screens/PiggyBank/hooks/usePiggyBank';
import styled, { keyframes, css } from 'styled-components';
import PiggyBankInfo from './components/PiggyBankInfo';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import PiggyBankAmount from './components/PiggyBankAmount';
import PiggyBankBreakButton from './components/PiggyBankBreakButton';
import PiggyBankCoinsAnimation from './components/PiggyBankCoinsAnimation';
import useHideMainScreen from './hooks/useHideMainScreen';

const PiggyBank = props => {
  const piggyBankData = usePiggyBank(props);
  const {
    piggyBankFeatureState,
    piggyBankAssets,
    openModal,
    setOpenModal,
    handleOpenModal,
    renderPiggyImage,
    renderBallonText,
    isBroken,
    availableToBreak,
    coinsAnimationRef,
  } = piggyBankData;
  const { closeInfoScreen, showMainScreen } = useHideMainScreen({ openModal, setOpenModal });

  return (
    <>
      {piggyBankFeatureState && (
        <ContainerBackground $showMainScreen={showMainScreen} source={piggyBankAssets?.PIGGY_BANK_POPUP_WINDOW}>
          <PiggyBankInfo {...piggyBankData} closeInfoScreen={closeInfoScreen} />
          <PiggyBankCoinsAnimation ref={coinsAnimationRef} />
          <Wrapper $openModal={openModal}>
            <Logo src={piggyBankAssets.PIGGY_BANK_HEADER_LOGO} />
            <InfoButton src={piggyBankAssets.PIGGY_BANK_INFO_BUTTON} onClick={handleOpenModal} />
            <CloseButton src={piggyBankAssets.PIGGY_BANK_CLOSE_BUTTON} onClick={props.dismissModal} />
            <Subtitle>{getText(TEXT_KEY.PIGGY_BANK_SUBTITLE)}</Subtitle>
            <PiggyBankAmount
              isBroken={isBroken}
              oldPurchasedVales={piggyBankFeatureState.lastPurchasedPackage}
              {...piggyBankFeatureState?.breakPackage}
            />
            $
            {!isBroken && (
              <BallonWrapper>
                <BalloonText
                  fontSizeLarge={renderBallonText.length > 21}
                  dangerouslySetInnerHTML={{ __html: renderBallonText }}
                />
              </BallonWrapper>
            )}
            {isBroken ? (
              <PiggyImageBroken src={piggyBankAssets.PIGGY_BANK_BROKEN} $play={isBroken} />
            ) : (
              <>
                {!!renderPiggyImage && <PiggyImageIntact src={renderPiggyImage} $breaking={isBroken} />}
                <BallonWrapper>
                  <BalloonText
                    fontSizeLarge={renderBallonText.length > 21}
                    dangerouslySetInnerHTML={{ __html: renderBallonText }}
                  />
                </BallonWrapper>
              </>
            )}
            {!isBroken && (
              <PiggyBankBreakButton
                purchasePackage={piggyBankFeatureState.breakPackage}
                availableToBreak={availableToBreak}
              />
            )}
          </Wrapper>
        </ContainerBackground>
      )}
    </>
  );
};

export default PiggyBank;

const ContainerBackground = styled.div.withConfig({
  shouldForwardProp: prop => !['source'].includes(prop),
})`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url(${({ source }) => source});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  opacity: ${({ $showMainScreen }) => ($showMainScreen ? 1 : 0)};
  transition: ${({ $showMainScreen }) => ($showMainScreen ? 'opacity 0.4s ease-in-out' : 'none')};
`;

const Wrapper = styled.div`
  position: relative;
  display: ${({ $openModal }) => (!$openModal ? 'flex' : 'none')};
  height: 36dvw;
  width: 60dvw;
  align-items: center;
  justify-content: center;
  ${({ theme }) =>
    theme.screens.device('mobile-browser') &&
    `
    height: 31dvw;
    width: 52dvw;
    `}
`;

const Logo = styled.img`
  width: 100%;
  height: 10dvw;
  object-fit: contain;
  position: absolute;
  top: -5dvw;
  ${({ theme }) =>
    theme.screens.device('mobile-browser') &&
    `
    height: 9dvw;
    top: -3dvw;
    `}
`;

const InfoButton = styled.img`
  width: 3dvw;
  height: 3dvw;
  object-fit: contain;
  position: absolute;
  top: 3dvw;
  left: 3dvw;
  cursor: pointer;
`;

const CloseButton = styled.img`
  width: 3dvw;
  height: 3dvw;
  object-fit: contain;
  position: absolute;
  top: 3dvw;
  right: 3dvw;
  cursor: pointer;
`;

const Subtitle = styled.span`
  top: 3dvw;
  position: absolute;
  text-align: center;
  line-height: 1.2;
  font-size: 2.5dvw;
  font-weight: 700;
  z-index: 1;
  text-transform: uppercase;
  letter-spacing: -1.5px;
  ${({ theme }) =>
    theme.screens.device('mobile-browser') &&
    `
    font-size: 2.2dvw;
    top: 3.5dvw;
    `}
`;

const BallonWrapper = styled.div`
  position: absolute;
  justify-content: center;
  align-items: center;
  z-index: 2;
  display: flex;
  height: 11dvw;
  width: 17dvw;
  top: 13.7dvw;
  right: 1dvw;
  ${({ theme }) =>
    theme.screens.device('mobile-browser') &&
    `
    top: 10.4dvw;
    right: -1dvw;
    `}
`;

const BalloonText = styled.span.withConfig({
  shouldForwardProp: prop => !['fontSizeLarge'].includes(prop),
})`
  color: #d82a49;
  font-size: ${({ fontSizeLarge }) => (fontSizeLarge ? '2dvw' : '3dvw')};
  text-align: center;
  line-height: 1.2;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -1.5px;

  ${({ theme, fontSizeLarge }) =>
    theme.screens.device('mobile-browser') &&
    `
      font-size: ${fontSizeLarge ? '1.8dvw' : '2.8dvw'};
    `}
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const fadeInScale = keyframes`
  0%   { opacity: 0; }
  100% { opacity: 1; }
`;

const piggyBase = css`
  width: 100%;
  height: 24dvw;
  object-fit: contain;
  position: absolute;
  bottom: -1dvw;
  left: 1dvw;
  will-change: opacity, transform;
  ${({ theme }) =>
    theme.screens.device('mobile-browser') &&
    `
    bottom: -2dvw;
    `}
`;

const PiggyImageIntact = styled.img.withConfig({
  shouldForwardProp: p => !['$breaking'].includes(p),
})`
  ${piggyBase}
  ${({ $breaking }) =>
    $breaking &&
    css`
      animation: ${fadeOut} 100ms ease-out forwards;
    `}
`;

const PiggyImageBroken = styled.img.withConfig({
  shouldForwardProp: p => !['$play'].includes(p),
})`
  ${piggyBase}
  opacity: 0;
  ${({ $play }) =>
    $play &&
    css`
      animation: ${fadeInScale} 1500ms ease-in forwards;
    `}
`;

PiggyBank.hideDefaultBackground = true;
PiggyBank.hideCloseButton = true;
PiggyBank.AsModalProps = { dialogClassName: 'gtp-modal-piggy-bank' };

